import { computed, inject, Injectable, signal } from '@angular/core';
import { Branding, ThemeMode, ThemeModes } from '@raptor-fe/core';
import { StorageKeys, StorageService } from '../storage-service/storage.service';
import { buildBrandTokens, buildBrandingOverrides, buildPrimaryExtras, hexToRgb } from './branding-css';

const BRANDING_STYLE_ID = 'raptor-branding-overrides';
const KENDO_THEME_ELEMENT_ID = 'kendo-theme';
const HTML_ELEMENT_ID = 'html';
const DATA_BS_THEME_ATTR = 'data-bs-theme';
const DATA_BRANDING_LOGO_ATTR = 'data-branding-logo';
const KENDO_CDN_BASE = 'https://unpkg.com/@progress/kendo-theme-bootstrap@14.5.0/dist/';
const KENDO_THEME_FILE: Record<ThemeMode, string> = {
    light: 'bootstrap-main.css',
    dark: 'bootstrap-main-dark.css',
};

/** Reads Bootstrap's body background for a theme from the live stylesheet; null when no stylesheet defines it. */
export function bodyBackgroundFor(theme: ThemeMode): string | null {
    const probe = document.createElement('div');
    probe.setAttribute(DATA_BS_THEME_ATTR, theme);
    probe.hidden = true;
    document.body.appendChild(probe);
    const value = getComputedStyle(probe).getPropertyValue('--bs-body-bg').trim();
    probe.remove();
    return value || null;
}

function ionicShade(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;
    const f = (i: number) => Math.round(parseInt(result[i], 16) * 0.88).toString(16).padStart(2, '0');
    return `#${f(1)}${f(2)}${f(3)}`;
}

function ionicTint(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;
    const f = (i: number) => {
        const c = parseInt(result[i], 16);
        return Math.round(c + (255 - c) * 0.1).toString(16).padStart(2, '0');
    };
    return `#${f(1)}${f(2)}${f(3)}`;
}

function ionicContrastColor(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '#ffffff';
    const luminance = (0.299 * parseInt(result[1], 16) + 0.587 * parseInt(result[2], 16) + 0.114 * parseInt(result[3], 16)) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
}

interface BrandingColorEntry {
    brandingKey: string;
    bsName: string;
    kendoName: string;
    raptorVar: string;
    ionVar: string;
}

const BRANDING_COLORS: BrandingColorEntry[] = [
    { brandingKey: 'primaryColor', bsName: 'primary', kendoName: 'primary', raptorVar: '--raptor-primary', ionVar: '--ion-color-primary' },
    { brandingKey: 'secondaryColor', bsName: 'secondary', kendoName: 'secondary', raptorVar: '--raptor-secondary', ionVar: '--ion-color-secondary' },
    { brandingKey: 'successColor', bsName: 'success', kendoName: 'success', raptorVar: '--raptor-success', ionVar: '--ion-color-success' },
    { brandingKey: 'infoColor', bsName: 'info', kendoName: 'info', raptorVar: '--raptor-info', ionVar: '--ion-color-info' },
    { brandingKey: 'warningColor', bsName: 'warning', kendoName: 'warning', raptorVar: '--raptor-warning', ionVar: '--ion-color-warning' },
    { brandingKey: 'dangerColor', bsName: 'danger', kendoName: 'error', raptorVar: '--raptor-danger', ionVar: '--ion-color-danger' },
];

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private readonly storageService = inject(StorageService);

    constructor() {
        this.storageService.getString(StorageKeys.SELECTED_THEME)?.then((result) => this.changeTheme((result.value as ThemeMode) || ThemeModes.Light));
    }

    public isDarkTheme = signal(false);
    public currentTheme = signal<ThemeMode>(ThemeModes.Light);
    public logoLightUrl = signal<string | null>(null);
    public logoDarkUrl = signal<string | null>(null);
    public currentLogoUrl = computed(() => (this.isDarkTheme() ? this.logoDarkUrl() : this.logoLightUrl()));

    changeTheme(theme: ThemeMode): void {
        const themeLinkElement = document.getElementById(KENDO_THEME_ELEMENT_ID) as HTMLLinkElement | null;
        const htmlElement = document.getElementById(HTML_ELEMENT_ID) as HTMLElement | null;

        this.isDarkTheme.set(theme === ThemeModes.Dark);
        htmlElement?.setAttribute(DATA_BS_THEME_ATTR, theme);
        if (themeLinkElement) {
            themeLinkElement.href = KENDO_CDN_BASE + KENDO_THEME_FILE[theme];
        }
        this.storageService.setString(StorageKeys.SELECTED_THEME, theme);
        this.currentTheme.set(theme);
    }

    /**
     * Flips between light and dark. The fallback applies to the stored value rather than to the
     * result object, because getString always resolves { value } and so is never itself falsy.
     */
    async toggleTheme(): Promise<void> {
        const stored = await this.storageService.getString(StorageKeys.SELECTED_THEME);
        const currentTheme = stored.value || ThemeModes.Light;
        const newTheme = currentTheme === ThemeModes.Light ? ThemeModes.Dark : ThemeModes.Light;
        this.changeTheme(newTheme);
    }

    applyBranding(branding: Partial<Branding>): void {
        const root = document.documentElement;
        const rules: string[] = [];

        for (const entry of BRANDING_COLORS) {
            const value = branding[entry.brandingKey as keyof typeof branding] as string | null | undefined;
            if (!value) continue;

            root.style.setProperty(entry.raptorVar, value);
            root.style.setProperty(`--bs-${entry.bsName}`, value);
            root.style.setProperty(entry.ionVar, value);
            root.style.setProperty(`${entry.ionVar}-rgb`, hexToRgb(value));
            const contrast = ionicContrastColor(value);
            root.style.setProperty(`${entry.ionVar}-contrast`, contrast);
            root.style.setProperty(`${entry.ionVar}-contrast-rgb`, hexToRgb(contrast));
            root.style.setProperty(`${entry.ionVar}-shade`, ionicShade(value));
            root.style.setProperty(`${entry.ionVar}-tint`, ionicTint(value));

            rules.push(buildBrandingOverrides(entry.bsName, entry.kendoName, value));
            if (entry.bsName === 'primary') {
                rules.push(buildPrimaryExtras(value));
            }
        }

        if (branding.brandBackgroundColor || branding.brandAccentColor) {
            rules.push(buildBrandTokens(branding.brandBackgroundColor ?? null, branding.brandAccentColor ?? null));
        }

        this.injectStyleSheet(rules.join('\n'));
        this.logoLightUrl.set(branding.logoLightUrl ?? null);
        this.logoDarkUrl.set(branding.logoDarkUrl ?? null);

        const hasCustomLogo = !!branding.logoLightUrl || !!branding.logoDarkUrl;
        if (hasCustomLogo) {
            document.documentElement.setAttribute(DATA_BRANDING_LOGO_ATTR, '');
        } else {
            document.documentElement.removeAttribute(DATA_BRANDING_LOGO_ATTR);
        }
    }

    clearBranding(): void {
        const root = document.documentElement;

        for (const entry of BRANDING_COLORS) {
            root.style.removeProperty(entry.raptorVar);
            root.style.removeProperty(`--bs-${entry.bsName}`);
            root.style.removeProperty(entry.ionVar);
            for (const ionSuffix of ['-rgb', '-contrast', '-contrast-rgb', '-shade', '-tint']) {
                root.style.removeProperty(`${entry.ionVar}${ionSuffix}`);
            }
            for (const suffix of ['', '-hover', '-active', '-emphasis', '-subtle', '-subtle-hover', '-subtle-active']) {
                root.style.removeProperty(`--kendo-color-${entry.kendoName}${suffix}`);
            }
        }

        document.getElementById(BRANDING_STYLE_ID)?.remove();
        document.documentElement.removeAttribute(DATA_BRANDING_LOGO_ATTR);
        this.logoLightUrl.set(null);
        this.logoDarkUrl.set(null);
    }

    private injectStyleSheet(css: string): void {
        let styleEl = document.getElementById(BRANDING_STYLE_ID) as HTMLStyleElement | null;
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = BRANDING_STYLE_ID;
            document.head.appendChild(styleEl);
        }
        styleEl.textContent = css;
    }
}
